// ✅ src/components/Company.js (seek + 더보기 버전)
import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "../css/Home.css";
import axios from "axios";
import AdLeft from "./AdLeft";
import AdRight from "./AdRight";
import CommentSection from "./CommentSection";
import { handleSearch } from "../utils/search";
import { getDaysDiff } from "../utils/dateUtils";
import { checkSessionUser } from "../utils/auth";
import { handleNewsClickUtil } from "../utils/newsClick";
import { fetchRankingUtil } from "../utils/ranking";
import Footer from "./Footer";
import Header from "./Header";

axios.defaults.withCredentials = true;

axios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("jwtToken");
      window.location.href = "/login";
    }
    return Promise.reject(err);
  },
);

// LocalDateTime 파라미터 안전화: "2026-02-06T12:34:56.789Z" → "2026-02-06T12:34:56"
const toLocalDateTimeParam = (value) => {
  if (!value) return null;
  if (typeof value === "string") return value.replace("Z", "").slice(0, 19);
  // 혹시 Date 객체로 들어오면 처리
  if (value instanceof Date)
    return value.toISOString().replace("Z", "").slice(0, 19);
  return String(value).replace("Z", "").slice(0, 19);
};

function Company() {
  const navigate = useNavigate();
  const { company } = useParams();

  const [userInfo, setUserInfo] = useState(null);
  const [id, setId] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const [urlInput, setUrlInput] = useState("");

  // ✅ seek 기반 목록
  const postsPerPage = 5;
  const [posts, setPosts] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(false);

  const [msg, setMsg] = useState("");

  // ✅ 랭킹
  const [rankings, setRankings] = useState([]);
  const [period, setPeriod] = useState("daily");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // ✅ JWT 설정 및 세션 유저 확인
  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      checkSessionUser(setUserInfo, setId, setIsLoggedIn);
    } else {
      delete axios.defaults.headers.common["Authorization"];
      setIsLoggedIn(false);
      alert("비정상적인 접근이 감지되었습니다.");
      navigate("/");
    }
  }, [navigate]);

  // ✅ 첫 페이지 로드 (cursor 없이)
  const loadFirst = useCallback(async () => {
    if (!company) return;

    setLoadingPosts(true);
    setMsg("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SPRING_API_URL}/api/company-posts/seek`,
        { params: { company, size: postsPerPage } },
      );

      setPosts(res.data?.content ?? []);
      setHasNext(!!res.data?.hasNext);
    } catch (err) {
      console.error("회사별 포스트 로드 실패:", err);
      setMsg(err.response?.data?.message ?? "불러오기 실패");
      setPosts([]);
      setHasNext(false);
    } finally {
      setLoadingPosts(false);
    }
  }, [company, postsPerPage]);

  // ✅ company 바뀌면 첫 로드
  useEffect(() => {
    if (company) loadFirst();
  }, [company, loadFirst]);

  // ✅ 더보기 (마지막 post 기준 cursor)
  const loadMore = useCallback(async () => {
    if (!company) return;
    if (!hasNext) return;
    if (loadingPosts) return;
    if (!Array.isArray(posts) || posts.length === 0) return;

    const last = posts[posts.length - 1];
    const lastCreatedAt = toLocalDateTimeParam(last.createdAt);
    const lastId = last.id;

    setLoadingPosts(true);
    setMsg("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_SPRING_API_URL}/api/company-posts/seek`,
        {
          params: {
            company,
            size: postsPerPage,
            lastCreatedAt,
            lastId,
          },
        },
      );

      const next = res.data?.content ?? [];
      setPosts((prev) => [...prev, ...next]);
      setHasNext(!!res.data?.hasNext);
    } catch (err) {
      console.error("더보기 실패:", err);
      setMsg(err.response?.data?.message ?? "더보기 실패");
    } finally {
      setLoadingPosts(false);
    }
  }, [company, hasNext, loadingPosts, posts, postsPerPage]);

  // ✅ 랭킹 로드
  useEffect(() => {
    fetchRankingUtil(period, setRankings, setLoading, setError);
  }, [period]);

  // ✅ 랭킹 자동 순환
  useEffect(() => {
    if (!Array.isArray(rankings) || rankings.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.min(rankings.length, 10));
    }, 5000);
    return () => clearInterval(interval);
  }, [rankings]);

  const handleNewsClick = (e, url, username) => {
    e.preventDefault();
    handleNewsClickUtil(url, username, navigate);
  };

  return (
    <div className="Home-container">
      <Header />

      {/* 좌측 광고 */}
      <div className="AdLeft-container">
        <AdLeft />
      </div>

      {/* 우측 광고 */}
      <div className="AdRight-container">
        <AdRight />
      </div>

      <div className="Home-main">
        <div className="Home-img"></div>

        <header className="Home-header">
          <div className="Home-home-container">
            <div className="Home-title-container">
              <button
                className="Home-home-button"
                type="button"
                onClick={() => navigate("/")}
              >
                <img
                  className="Home-home-button-img"
                  src="/snake.png"
                  alt="타이틀 이미지"
                />
              </button>
            </div>
          </div>

          <p className="Home-body">{company}의 커뮤니티</p>

          {/* 검색 */}
          <div className="Home-search-container">
            <div className="Home-Search" style={{ width: "800px" }}>
              <input
                id="Home-search-input"
                style={{ width: "1000px" }}
                placeholder="뉴스 URL을 입력하세요."
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearch(urlInput, id, navigate);
                  }
                }}
              />
              <img
                className="Home-search-img"
                src="/icon.png"
                alt="돋보기"
                onClick={() => handleSearch(urlInput, id, navigate)}
              />
            </div>
          </div>

          {!isLoggedIn ? (
            <p className="Home-tail">
              로그인해서 전에 검색했던 내용을 다시 확인하세요!
            </p>
          ) : (
            <>
              {msg && <p className="Home-tail">{msg}</p>}

              {/* 목록 */}
              <div className="Home-news-list">
                {Array.isArray(posts) &&
                  posts.map((post) => (
                    <div key={post.id} className="Home-news-container">
                      <div className="Home-news-img-container">
                        <img
                          className="Home-news-img"
                          src={post.thumbnailUrl || "/default-thumbnail.png"}
                          alt="썸네일"
                        />
                      </div>

                      <div className="Home-news-detail-container">
                        <h2 className="Home-news-title">
                          <Link
                            to="/news"
                            state={{ url: post.url }}
                            onClick={(e) => handleNewsClick(e, post.url)}
                          >
                            {post.title}
                          </Link>
                        </h2>

                        <p className="Home-news-summary">
                          <Link
                            to="/news"
                            state={{ url: post.url }}
                            onClick={(e) => handleNewsClick(e, post.url)}
                          >
                            {post.content}
                          </Link>
                        </p>

                        <p className="Home-news-time">
                          {getDaysDiff(new Date(), post.newsTime) < 32
                            ? `${getDaysDiff(new Date(), post.newsTime)}일 전`
                            : post.newsTime}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>

              {/* 더보기 */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "16px",
                }}
              >
                {hasNext ? (
                  <button
                    className="page-btn"
                    type="button"
                    onClick={loadMore}
                    disabled={loadingPosts}
                  >
                    {loadingPosts ? "불러오는 중..." : "더보기"}
                  </button>
                ) : (
                  posts.length > 0 && (
                    <p className="Home-tail" style={{ marginTop: "8px" }}>
                      마지막 페이지입니다.
                    </p>
                  )
                )}
              </div>

              {/* 댓글 */}
              <CommentSection company={company} />
            </>
          )}
        </header>
      </div>

      <Footer />
    </div>
  );
}

export default Company;
